import React, { useEffect, useState } from 'react';
import CalendarPanel from './CalendarPanel';
import AssemblyDrawer from './AssemblyDrawer';
import CreateAssemblyModal from './CreateAssemblyModal';
import OperationalMetricsPanel from './OperationalMetricsPanel';
import NewspaperPanel from './NewspaperPanel';
import StatusBadge from '../../components/ui/StatusBadge';
import { fmtDate } from '../../utils/dateUtils';
import { withAuthToken } from '../../utils/fileUrl';

const fullName = u =>
  u ? `${u.firstName} ${u.lastNamePaternal} ${u.lastNameMaternal}`.trim() : '';

function MyAssemblyHistoryPanel({ items, loading }) {
  if (loading) {
    return (
      <section className="my-reports-page">
        <div className="my-reports-header">
          <h2>Mis asambleas</h2>
        </div>
        <p className="my-report-empty">Cargando historial de asambleas...</p>
      </section>
    );
  }

  if (!items.length) {
    return (
      <section className="my-reports-page">
        <div className="my-reports-header">
          <h2>Mis asambleas</h2>
        </div>
        <p className="my-report-empty">Aún no tienes asambleas registradas.</p>
      </section>
    );
  }

  return (
    <section className="my-reports-page">
      <div className="my-reports-header">
        <h2>Mis asambleas</h2>
      </div>

      <div className="my-reports-list">
        {items.map(({ assembly, report, evidences }) => {
          const photoEvidences = evidences.filter((ev) => ev.fileType === 'photo');
          const documentEvidences = evidences.filter((ev) => ev.fileType !== 'photo');

          return (
            <article key={assembly.id} className="my-report-card">
              <div className="my-report-head">
                <div>
                  <h3>{assembly.address}</h3>
                  <div className="my-report-meta">
                    {fmtDate(assembly.scheduledDate)} · {assembly.startTime}–{assembly.endTime}
                  </div>
                </div>
                <StatusBadge status={assembly.status} />
              </div>

              <div className="my-report-summary">
                <div className="my-report-summary-box">
                  <strong>Asistentes</strong>
                  {report ? report.attendeesCount : '—'}
                </div>
                <div className="my-report-summary-box">
                  <strong>Fecha real</strong>
                  {report ? fmtDate(report.actualDate) : 'Sin informe'}
                </div>
                <div className="my-report-summary-box">
                  <strong>Status</strong>
                  {report ? (report.status === 'submitted' ? 'Enviado' : 'Borrador') : 'Sin informe'}
                </div>
              </div>

              {report ? (
                <div className="my-report-docs">
                  <strong>Informe:</strong>
                  <div>{report.objective || 'Sin objetivo registrado.'}</div>
                  <div>{report.activitiesPerformed || 'Sin actividades registradas.'}</div>
                </div>
              ) : (
                <p className="my-report-empty">Todavía no hay informe para esta asamblea.</p>
              )}

              {photoEvidences.length > 0 && (
                <div className="my-report-photos">
                  {photoEvidences.map((photo) => (
                    <img
                      key={photo.id}
                      className="my-report-photo"
                      src={withAuthToken(photo.fileUrl)}
                      alt={photo.originalName}
                    />
                  ))}
                </div>
              )}

              {documentEvidences.length > 0 && (
                <div className="my-report-docs">
                  <strong>Documentos:</strong>
                  {documentEvidences.map((doc) => (
                    <a key={doc.id} href={withAuthToken(doc.fileUrl)} target="_blank" rel="noreferrer">
                      {doc.originalName}
                    </a>
                  ))}
                </div>
              )}

              {!report && documentEvidences.length === 0 && photoEvidences.length === 0 && (
                <p className="my-report-empty">Sin fotos ni documentos adjuntos.</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function Dashboard({
  toast,
  auth,
  ctx,
  initialView = 'dashboard',
  onBackToAdmin,
}) {
  const [activeView, setActiveView] = useState(initialView);
  const { user, logout } = auth;
  const {
    assemblies, loading,
    operationalMetrics,
    metricsLoading,
    metricsDays,
    setMetricsDays,
    refreshOperationalMetrics,
    myAssemblyHistory,
    myAssemblyHistoryLoading,
    showModal, setShowModal, modalForm, setModalForm, modalSaving,
    drawerAssembly,
    openCreateModal, handleCreateAssembly, handleClickEvent,
    reset,
  } = ctx;

  useEffect(() => {
    setActiveView(initialView);
  }, [initialView]);

  return (
    <div className="app-shell">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <header className="app-header">
        <span className="header-title">
          Asambleas Informativas por la Defensa de la Soberanía Nacional
        </span>
        <div className="header-user">
          <div className="user-info">
            <strong>{fullName(user)}</strong>
            <span>Coordinador · {user.state}, {user.municipality}</span>
          </div>
          {typeof onBackToAdmin === 'function' && (
            <button className="btn-ghost" onClick={onBackToAdmin}>Volver a Admin</button>
          )}
          <button className="btn-ghost" onClick={() => logout(reset)}>Cerrar sesión</button>
        </div>
      </header>

      <div className="app-body">
        <div className="dashboard-main-content">
          <div className="dashboard-view-switch" role="tablist" aria-label="Cambiar vista principal">
            <button
              className={activeView === 'dashboard' ? 'active' : ''}
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={activeView === 'calendar' ? 'active' : ''}
              onClick={() => setActiveView('calendar')}
            >
              Calendario
            </button>
            <button
              className={activeView === 'newspaper' ? 'active' : ''}
              onClick={() => setActiveView('newspaper')}
            >
              Periodico
            </button>
          </div>

          {activeView === 'dashboard' && (
            <>
              <div className="dashboard-metrics-page">
                <OperationalMetricsPanel
                  metrics={operationalMetrics}
                  loading={metricsLoading}
                  days={metricsDays}
                  onChangeDays={setMetricsDays}
                  onRefresh={refreshOperationalMetrics}
                  isAdmin={Boolean(user?.isAdmin)}
                />
              </div>

              <MyAssemblyHistoryPanel
                items={myAssemblyHistory}
                loading={myAssemblyHistoryLoading}
              />
            </>
          )}

          {activeView === 'calendar' && (
            <CalendarPanel
              assemblies={assemblies}
              loading={loading}
              onDayClick={openCreateModal}
              onEventClick={handleClickEvent}
            />
          )}

          {activeView === 'newspaper' && <NewspaperPanel ctx={ctx} user={user} />}
        </div>
        {activeView === 'calendar' && drawerAssembly && <AssemblyDrawer ctx={ctx} />}
      </div>

      {showModal && (
        <CreateAssemblyModal
          form={modalForm}
          setForm={setModalForm}
          onSubmit={handleCreateAssembly}
          saving={modalSaving}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
